import { useEffect, useState } from 'react';

export type Direction = 'ltr' | 'rtl';

/**
 * Result returned from the useDirection hook
 */
export interface UseDirectionResult {
  /**
   * The current document direction, either 'ltr' or 'rtl'.
   */
  direction: Direction;
  /**
   * Convenience boolean that is true when the direction is 'rtl'.
   */
  isRTL: boolean;
}

function readDirection(): Direction {
  if (typeof document === 'undefined') return 'ltr';
  const dir = document.documentElement.getAttribute('dir');
  return dir === 'rtl' ? 'rtl' : 'ltr';
}

/**
 * Custom hook that returns the current document direction ('ltr' or 'rtl').
 *
 * Replacement for the legacy `getDirection()` utility. Unlike the utility,
 * this hook is SSR-safe (always renders 'ltr' on the server and on the first
 * client render) and reacts to runtime changes of the `dir` attribute on
 * `<html>` via a `MutationObserver`.
 *
 * @returns An object containing the current `direction` string and an `isRTL` flag.
 *
 * @example
 * ```tsx
 * const { direction, isRTL } = useDirection();
 *
 * return <div dir={direction}>{isRTL ? 'مرحبا' : 'Hello'}</div>;
 * ```
 */
export function useDirection(): UseDirectionResult {
  const [direction, setDirection] = useState<Direction>('ltr');

  useEffect(() => {
    setDirection(readDirection());

    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      return;
    }

    const target = document.documentElement;
    const observer = new MutationObserver(() => {
      setDirection(prev => {
        const next = readDirection();
        return next === prev ? prev : next;
      });
    });

    observer.observe(target, { attributes: true, attributeFilter: ['dir'] });

    return () => observer.disconnect();
  }, []);

  return { direction, isRTL: direction === 'rtl' };
}
